import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  TextField,
  Box,
  FormControl,
  InputLabel,
  TableFooter,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import AddIcon from "@mui/icons-material/Add";
import Modal from "@mui/material/Modal";
import { styled } from "@mui/material/styles";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { InputTextarea } from "primereact/inputtextarea";
import RulePage from "./RulesPage";
import { useLoader } from "../common/LoaderContext";
import CircleIcon from '@mui/icons-material/Circle';
import SearchIcon from '@mui/icons-material/Search';
import ViewValidationResult from "./ViewValidationResult";
import { useNavigate } from 'react-router-dom';
import { getInsights, downloadPayrollData, getAnalysisLogs } from "../services/dashboard.services";

interface DashboardValidationReportsProps {
  reports: any[];
}

const DashboardValidationReports: React.FC<DashboardValidationReportsProps> = ({ reports }) => {

  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();

  const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
      maxWidth: 221,
      fontSize: "12px",
      padding: "8px 12px",
    },
  }));

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [payScaleTypeFilter, setPayScaleTypeFilter] = useState("All");
  const [personNoFilter, setPersonNoFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Pagination slice
  const pagedData = useMemo(() => {
    const start = page * rowsPerPage;
    return reports.slice(start, start + rowsPerPage);
  }, [reports, page, rowsPerPage]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setPayScaleTypeFilter("All");
    setStatusFilter("All");
    setPersonNoFilter("All");
    setSearchText("");
    setPage(0);
  };

  const handleValidateNewPayroll = () => {
    navigate("/rule", { state: { activeStep: 2 } });
  };

  const handleViewValidation = async (notification_id: string, doc_name: string, session: string) => {
    try {
      const res = await getInsights(notification_id);
      navigate("/insights", { state: { validationData: res, docName: doc_name, session: session } });
    } catch (error: any) {
      console.error("Validation fetch error:", error.message);
    }
  };

  const handleDownloadPayrollData = async (docName: string, uploadId: string) => {
    try {
      const blob = await downloadPayrollData(docName, uploadId);
      let extension = "";
      if (blob.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        extension = "xlsx";
      } else if (blob.type === "application/zip" || blob.type === "application/x-zip-compressed") {
        extension = "zip";
      } else {
        extension = docName.split(".").pop() || "bin";
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${docName}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Download error:", error.message);
    } finally {
      hideLoader();
    }
  };

  const handleDownloadAnalysisLogs = async (doc_name: string, session: string) => {
    try {
      const blob = await getAnalysisLogs(doc_name, session);
      const fileBlob = new Blob([blob], { type: 'text/plain' });
      const url = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${doc_name}_${session}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Download error:", error.message);
    }
  };

  return (<>
    <div className="page-header-container">
      <div className="left-section">
        <div className="page-heading-section">
          <label className="page-heading-sub">Validation Reports</label>
        </div>
      </div>
      <div className="right-section">
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          sx={{ textTransform: "none", width: '201px !important', color:'#007CB0',borderColor:'#007CB0' }}
          onClick={handleValidateNewPayroll}
        >
          Validate New Payroll
        </Button>
      </div>
    </div>
    <Box sx={{ padding: "0px 72px 72px 72px" }}>
      <div className="card dark-table">
        <div
          style={{
            paddingTop: "24px",
            color: "#fff",
          }}
        >
          {/* Filters and Search Top Bar */}


          <TableContainer sx={{ bgcolor: "#0F1116", borderTop: "none",borderRadius:'8px' }}>
            <Table className="manage-rules-tbl">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1C1E22" }}>
                  <TableCell
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderBottom: "none",
                      borderRight: "1px solid #2F3337"
                    }}
                  >
                    File Name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderBottom: "none",
                      borderRight: "1px solid #2F3337"
                    }}
                  >
                    File type
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderBottom: "none",
                      borderRight: "1px solid #2F3337"
                    }}
                  >
                    Session
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderBottom: "none",
                      borderRight: "1px solid #2F3337"
                    }}
                  >
                    Start date
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderBottom: "none",
                      borderRight: "1px solid #2F3337"
                    }}
                  >
                    End date
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderBottom: "none",
                      borderRight: "1px solid #2F3337"
                    }}
                  >
                    Rule version
                  </TableCell>

                  <TableCell
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderBottom: "none",
                      borderRight: "1px solid #2F3337",
                      textAlign: "center"
                    }}
                  >
                    KPI
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderBottom: "none",
                      borderRight: "1px solid #2F3337"
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderBottom: "none",
                      borderRight: "1px solid #2F3337"
                    }}
                  >
                    Action
                  </TableCell>

                  {/* <TableCell
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "14px",
                          borderBottom: "none"
                        }}
                      >
                        Action
                      </TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedData.map((row, index) => (
                  <TableRow
                    sx={{
                      backgroundColor:
                        index % 2 === 0 ? "#26292E" : "#1C1E22"
                    }}
                  >
                    <TableCell
                      sx={{
                        color: "#BFC1C2",
                        fontWeight: 400,
                        fontSize: "16px",
                        borderBottom: "none",
                        borderRight: "1px solid #2F3337"
                      }}
                    >
                      {row.filename}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#BFC1C2",
                        fontWeight: 400,
                        fontSize: "16px",
                        borderBottom: "none",
                        borderRight: "1px solid #2F3337"
                      }}
                    >
                      {row.filetype}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#BFC1C2",
                        fontWeight: 400,
                        fontSize: "16px",
                        borderBottom: "none",
                        borderRight: "1px solid #2F3337"
                      }}
                    >
                      {row.session}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#BFC1C2",
                        fontWeight: 400,
                        fontSize: "16px",
                        borderBottom: "none",
                        borderRight: "1px solid #2F3337"
                      }}
                    >
                      {row.start_date}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#BFC1C2",
                        fontWeight: 400,
                        fontSize: "16px",
                        borderBottom: "none",
                        borderRight: "1px solid #2F3337"
                      }}
                    >
                      {row.end_date}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#BFC1C2",
                        fontWeight: 400,
                        fontSize: "16px",
                        borderBottom: "none",
                        borderRight: "1px solid #2F3337"
                      }}
                    >
                      {typeof row.rule_version === "object"
                        ? JSON.stringify(row.rule_version)
                        : row.rule_version}
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "#BFC1C2",
                        fontWeight: 400,
                        fontSize: "16px",
                        borderBottom: "none",
                        borderRight: "1px solid #2F3337",
                        textAlign: "center"
                      }}
                    >

                      <BootstrapTooltip
                        title={
                          row.kpi
                            ? Object.entries(row.kpi)
                              .map(([key, val]) => `${key}: ${val}`)
                              .join(" | ")
                            : "No KPI data"
                        }
                      >
                        <IconButton color="primary" size="small">
                          <img src="./icons/info.svg" />
                        </IconButton>
                      </BootstrapTooltip>

                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#BFC1C2",
                        fontWeight: 400,
                        fontSize: "16px",
                        borderBottom: "none",
                        borderRight: "1px solid #2F3337",
                        width: "8em",
                      }}
                    >

                      {row.status.toLocaleLowerCase() == "in progress" ?
                        <CircleIcon sx={{ width: 10, height: 10, mr: '9px', color: '#DD9446' }} /> :
                        row.status.toLocaleLowerCase() == "completed" ?
                          <CircleIcon sx={{ width: 10, height: 10, mr: '9px', color: '#26890D' }} /> :
                          row.status.toLocaleLowerCase() == "failed" ?
                            <CircleIcon sx={{ width: 10, height: 10, mr: '9px', color: '#E66649' }} /> :
                            <CircleIcon sx={{ width: 10, height: 10, mr: '9px', color: '#9f9f9f' }} />
                      }

                      {row.status}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#BFC1C2",
                        fontWeight: 400,
                        fontSize: "16px",
                        borderBottom: "none",
                        display: "flex",
                      }}
                      align="center"

                    >
                      {row.status !== "Failed" && (<div style={{ borderRight: '1px solid #2F3337', paddingRight: '8px' }}>
                        <BootstrapTooltip
                          title="Insights"
                        >
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleViewValidation(row.notif_id, row.doc_name, row.session)}
                        >
                          <img src="./icons/file.svg" />
                        </IconButton>
                        </BootstrapTooltip>
                      </div>)}

                      {row.status == "Failed" && (<div style={{ borderRight: '1px solid #2F3337', paddingRight: '8px' }}>
                        <BootstrapTooltip
                          title="Download Logs"
                        >
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleDownloadAnalysisLogs(row.doc_name, row.session)}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                        </BootstrapTooltip>
                      </div>)}

                      <div style={{ paddingLeft: '8px' }}>
                        <BootstrapTooltip
                          title="Download File"
                        >
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleDownloadPayrollData(row.doc_name, row.upload_id)}
                        >
                          <img src="./icons/download.svg" />
                        </IconButton>
                        </BootstrapTooltip>
                      </div>




                    </TableCell>

                  </TableRow>
                ))}
                {reports.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ color: "#aaa" }}
                    >
                      No records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ borderBottom: "none" }}>
                  <TablePagination
                    sx={{
                      color: "#fff",
                      borderBottom: "none",
                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .css-h7cjts-MuiButtonBase-root-MuiIconButton-root.Mui-disabled":
                        { color: "#fff" },
                      "& .MuiIconButton-root": { color: "#fff" },
                      "& .MuiSelect-select": { color: "#fff" },
                      alignItems: "center",
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    count={reports.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Rows per page"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} of ${count}`
                    }
                    showFirstButton
                    showLastButton
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </div>


      </div>
    </Box>


  </>
  );


}

export default DashboardValidationReports;